'use client';
import { useTranslations } from 'next-intl';
import { useId, useMemo, useRef, useState } from 'react';
import { ProductLineUserGuide } from '@/app/components/icons/ProductLineUserGuide';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { FormSubmitButton } from '@/app/components/shared/FormSubmitButton';
import { useToast } from '@/app/components/ui/use-toast';
import { AUDIO_DURATION, isEnterpriseDistribution, SITE } from '@/app/constants';
import { useFetchRecreateData } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { getText2AudioMaxValidSegmentEndTime } from '@/app/utils';
import { TaskHistory } from '../../_components/TaskHistory';
import { TimelineEditor } from './_components/TimelineEditor';
import { TimestampTextArea, type TimestampTextAreaRef } from './_components/TimestampTextArea';
import { T2A_FORM_BG, T2A_LIMIT, T2A_USER_GUIDE_URL } from './constant';

export default function Text2AudioForm() {
  const t = useTranslations();
  const { toast } = useToast();
  const { trialsText2Audio } = useStore((state) => ({
    trialsText2Audio: state.trialsText2Audio,
  }));

  const timestampTextAreaRef = useRef<TimestampTextAreaRef>(null);

  const [disabledReason, setDisabledReason] = useState<
    'noPrompt' | 'timestampOutOfRange' | 'timestampOverTip' | 'timeTagNoPrompt' | 'promptNoTimeTag' | undefined
  >(undefined);

  const { formParams, updateText2AudioFormParams } = useStore((state) => ({
    formParams: state.text2audioFormParams,
    updateText2AudioFormParams: state.updateText2AudioFormParams,
  }));

  useFetchRecreateData();

  const disabled = useMemo(() => {
    // 检查是否有非空文本
    const hasText = formParams.segments?.some((item) => item.text?.trim() !== '');
    if (!hasText) {
      setDisabledReason('noPrompt');
      return true;
    }

    // 仅有一段prompt但是没有时间戳的情况允许提交
    const hasOnlyOnePromptWithoutTimeTag =
      formParams.segments?.length === 1 &&
      formParams.segments[0].startTime === 0 &&
      formParams.segments[0].endTime === 0;
    if (hasOnlyOnePromptWithoutTimeTag) {
      setDisabledReason(undefined);
      return false;
    }

    // 检查是否存在有文本但没有时间戳的段落
    const hasTextWithoutTimestamp = formParams.segments?.some((item) => {
      const hasText = item.text?.trim() !== '';
      // 只有当开始时间和结束时间都为0时，才认为是没有时间戳
      const hasNoTimestamp = item.startTime === 0 && item.endTime === 0;
      return hasText && hasNoTimestamp;
    });
    if (hasTextWithoutTimestamp) {
      setDisabledReason('promptNoTimeTag');
      return true;
    }

    // 检查是否存在有时间戳但没有文本的段落
    const hasTimestampWithoutText = formParams.segments?.some((item) => {
      const hasNoText = !item.text?.trim();
      const hasTimestamp = item.startTime !== 0 || item.endTime !== 0;
      return hasNoText && hasTimestamp;
    });
    if (hasTimestampWithoutText) {
      setDisabledReason('timeTagNoPrompt');
      return true;
    }

    // 检查所有时间戳是否都在范围内
    const duration = formParams.settings?.duration ?? AUDIO_DURATION;
    const hasOutOfRange = formParams.segments?.some((segment) => {
      return (segment.endTime ?? 0) > duration;
    });
    if (hasOutOfRange) {
      setDisabledReason('timestampOutOfRange');
      return true;
    }

    // 时间戳大于等于15个禁止提交
    const isTimestampCountExceeded = (formParams.segments?.length ?? 0) >= T2A_LIMIT.TIMESTAMP_COUNT;
    if (isTimestampCountExceeded) {
      setDisabledReason('timestampOverTip');
      return true;
    }

    setDisabledReason(undefined);
    return false;
  }, [formParams]);

  const judgeCanFormParamsSubmit = () => {
    // 只有一段prompt但是没有时间戳的情况
    if (
      formParams.segments?.length === 1 &&
      formParams.segments[0].startTime === 0 &&
      formParams.segments[0].endTime === 0
    ) {
      return true;
    }

    // 检查所有时间间隔是否不小于1秒
    const hasInvalidInterval = formParams.segments?.some((segment) => {
      const startTime = segment.startTime ?? 0;
      const endTime = segment.endTime ?? 0;
      return endTime - startTime <= T2A_LIMIT.INTERVAL_LIMIT;
    });
    if (hasInvalidInterval) {
      toast({
        type: 'warning',
        message: t('text2audio.timelineEditor.intervalTooShort', { limit: T2A_LIMIT.INTERVAL_LIMIT }),
      });
      return true;
    }

    // 时间戳超过10个能提交不拦截
    const isTimestampCountExceeded = (formParams.segments?.length ?? 0) > T2A_LIMIT.TIMESTAMP_TIP_COUNT;
    if (isTimestampCountExceeded) {
      toast({
        type: 'warning',
        message: t('text2audio.create.timestampOverTip'),
      });
      return true;
    }

    // 在提交前获取所有的音频段最大值设置成总时长
    const maxEndTime = getText2AudioMaxValidSegmentEndTime(formParams.segments);
    const currentDuration = formParams.settings?.duration ?? AUDIO_DURATION;

    if (maxEndTime < currentDuration) {
      updateText2AudioFormParams({ settings: { duration: maxEndTime } });

      toast({
        type: 'info',
        message: t('text2audio.autoAdjustDuration'),
      });
    }

    return true;
  };

  const scrollableTargetId = useId();

  return (
    <div
      id={scrollableTargetId}
      className="mx-auto flex h-full w-full max-w-full gap-3 px-4 pb-6 sm:h-full sm:flex-col sm:items-center sm:gap-8 sm:overflow-y-auto sm:px-3 lg:px-6 lg:pb-4"
    >
      <div className="relative w-[38%] max-w-[488px] sm:w-full sm:min-w-[334px] md:max-w-full lg:min-w-[384px]">
        <div className="flex h-full w-full flex-col overflow-y-auto pr-2 pb-[72px] sm:h-auto sm:min-h-[420px] sm:pr-0">
          <div
            style={{ backgroundImage: `url(${T2A_FORM_BG[SITE]})` }}
            className="relative flex w-full flex-1 flex-col gap-3 rounded-12 bg-cover bg-system-bg04 p-4 text-sm md:p-3"
          >
            <div className="px-2 py-1">
              <h3 className="flex items-center justify-between font-medium text-white text-xl leading-[1.4]">
                {t('text2audio.title')}
                {!isEnterpriseDistribution && (
                  <ClientLink
                    href={T2A_USER_GUIDE_URL[SITE]}
                    className="group flex cursor-pointer items-center gap-2 font-normal"
                    target="_blank"
                  >
                    <ProductLineUserGuide className="text-lg text-white opacity-80 group-hover:opacity-100" />
                    <span className="text-white/80 text-xs group-hover:text-white">{t('text2audio.userGuide')}</span>
                  </ClientLink>
                )}
              </h3>
              <p className="mt-1 font-normal text-sm text-system-white48 leading-[1.4]">
                {t('text2audio.description')}
              </p>
            </div>

            <TimestampTextArea
              ref={timestampTextAreaRef}
              prompts={formParams.segments}
              onChange={(segments, totalDuration) => {
                updateText2AudioFormParams({
                  segments,
                  settings: { duration: totalDuration ?? formParams.settings?.duration },
                });
              }}
            />

            <TimelineEditor
              maxDuration={AUDIO_DURATION}
              duration={formParams.settings?.duration}
              data={formParams.segments?.filter((segment) => !(segment.startTime === 0 && segment.endTime === 0))}
              onChange={(prompts) => {
                updateText2AudioFormParams({ segments: prompts });
              }}
              onClickNewSegment={(segment) => {
                timestampTextAreaRef.current?.handleClickTimestamp(segment);
              }}
              onDurationChange={(totalDuration) => {
                updateText2AudioFormParams({ settings: { duration: totalDuration } });
              }}
            />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 z-20 mt-4 flex w-full justify-center px-10 [background:linear-gradient(180deg,rgba(2,11,19,0)_0%,#020B13_76.04%)] sm:px-0 lg:mt-3">
          <FormSubmitButton
            taskType="text2audio"
            onBeforeSubmit={judgeCanFormParamsSubmit}
            disabled={disabled}
            disabledTip={disabledReason ? t(`text2audio.create.${disabledReason}`) : undefined}
            extra={
              trialsText2Audio?.remaining && !disabled ? (
                <div className="absolute top-0 right-0 rounded-bl-12 bg-system-blue02/80 px-[14px] py-[2px] font-medium text-sm text-white">
                  {t('text2audio.freeTrialLeft', { time: trialsText2Audio?.remaining })}
                </div>
              ) : undefined
            }
            displayCredits={!trialsText2Audio?.remaining}
          >
            {trialsText2Audio?.remaining ? t('text2audio.freeTrial') : t('create.createForm.submit')}
          </FormSubmitButton>
        </div>
      </div>

      <TaskHistory text2audio scrollableTargetId={scrollableTargetId} />
    </div>
  );
}
